import { HECKLES, curseName, heckleText } from "../../../../engine/bench";
import { displayName, roundByNo } from "../../../../engine/selectors";
import type { PlayerStatus, TournamentState } from "../../../../engine/types";

function names(list: string[]): string {
  if (list.length === 1) return list[0] as string;
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list[0]}, ${list[1]} and ${list.length - 2} more`;
}

function courts(list: number[]): string {
  if (list.length === 1) return `court ${list[0]}`;
  return `courts ${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

/**
 * What happened between two states, in words for the toast: arrivals, breaks, leavers, a round
 * closing with scores owed, a round starting, a typed heckle (so it can be removed), a curse,
 * curses or mini games switched off or on.
 * (A twist starts by the clock, so it has its own line at the end.)
 * Null when nothing worth saying.
 */
export function changes(prev: TournamentState, next: TournamentState): string | null {
  const said: string[] = [];
  const moved: Record<"here" | "break" | "back" | "left", string[]> = { here: [], break: [], back: [], left: [] };
  for (const p of Object.values(next.players)) {
    const was: PlayerStatus | undefined = prev.players[p.id]?.status;
    if (was === p.status) continue;
    const who = displayName(p, "admin");
    if (p.status === "in") moved[was === "break" || was === "left" ? "back" : "here"].push(who);
    else if (p.status === "break") moved.break.push(who);
    else if (p.status === "left") moved.left.push(who);
  }
  const many = (list: string[]) => list.length > 1;
  if (moved.here.length) said.push(`${names(moved.here)} ${many(moved.here) ? "are" : "is"} here.`);
  if (moved.back.length) said.push(`${names(moved.back)} ${many(moved.back) ? "are" : "is"} back.`);
  if (moved.break.length) said.push(`${names(moved.break)} ${many(moved.break) ? "are" : "is"} on a break.`);
  if (moved.left.length) said.push(`${names(moved.left)} ${many(moved.left) ? "have" : "has"} left.`);

  const was = prev.clock.roundNo;
  if (was !== null && prev.clock.phase !== "idle" && (next.clock.phase === "idle" || next.clock.roundNo !== was)) {
    const round = roundByNo(next, was);
    const owed = round ? round.games.filter((g) => !g.voided && g.score === null).map((g) => g.court) : [];
    if (owed.length) {
      said.push(`Round ${was}: ${courts(owed)}'s ${owed.length === 1 ? "score is" : "scores are"} still owed.`);
    }
  }
  if (next.rounds.length > prev.rounds.length) {
    const started = next.rounds[next.rounds.length - 1];
    if (started) said.push(`Round ${started.no} started.`);
  }
  // a heckle's seq is its event's, so anything past the old state's seq is new
  for (const h of next.bench.heckles) {
    if (h.seq <= prev.seq || HECKLES.some((_, i) => heckleText(i, h.court) === h.text)) continue;
    const who = displayName(next.players[h.by], "admin");
    said.push(`${who} typed a heckle: "${h.text}" Remove it on Events if it should not be up.`);
  }
  for (const c of next.bench.curses) {
    if (prev.bench.curses.some((x) => x.by === c.by && x.roundNo === c.roundNo && x.court === c.court)) continue;
    const pair = roundByNo(next, c.roundNo)?.games.find((g) => g.court === c.court)?.[c.side];
    const who = pair ? pair.map((id) => displayName(next.players[id], "admin")).join(" & ") : "a pair";
    const alone = c.stepsOff && pair ? pair.find((id) => id !== c.stepsOff) : undefined;
    const what = alone ? `solo, ${displayName(next.players[alone], "admin")} plays alone` : curseName(c).toLowerCase();
    said.push(`${displayName(next.players[c.by], "admin")} cursed ${who} on court ${c.court}: ${what}.`);
  }
  // another admin flipped a switch on the Events tab
  if (Boolean(prev.bench.cursesOff) !== Boolean(next.bench.cursesOff)) {
    said.push(next.bench.cursesOff ? "Curses are off for now." : "Curses are back on.");
  }
  if (Boolean(prev.miniGamesOff) !== Boolean(next.miniGamesOff)) {
    said.push(next.miniGamesOff ? "Mini games are off for now." : "Mini games are back on.");
  }
  return said.length ? said.join(" ") : null;
}

/** The toast for a twist that has just started. */
export function twistStarted(name: string, ms: number): string {
  return `Twist on every court: ${name}, ${Math.round(ms / 1000)} seconds.`;
}
